import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  CalendarClock,
  Check,
  Mail,
  Palette,
  Timer,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";
import { api, ApiError } from "../../lib/api";
import { loadSession } from "../../lib/auth";
import { AUTOMATIK_FELDER, automatikWerte } from "../../lib/automatik";
import { ARBEITSZEIT_STANDARD, WOCHENTAGE, arbeitszeitWerte, type Arbeitszeit } from "../../lib/arbeitszeiten";

interface Company {
  rechnung_steuernummer?: string | null;
  rechnung_ustid?: string | null;
  rechnung_iban?: string | null;
  rechnung_bic?: string | null;
  rechnung_zahlungsziel_tage?: number | null;
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  address: string | null;
  website: string | null;
  primary_color: string;
  greeting_style: "sie" | "du";
  tracking_page_url: string | null;
  email_signature: string | null;
  email_sender_name: string | null;
  notification_email: string | null;
  notfall_notification: boolean;
  bewertung_link?: string | null;
  datenschutz_url?: string | null;
  sla?: unknown;
}

export function Settings() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: () => api<Company>("/admin/settings"),
  });

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api("/admin/settings", { method: "PUT", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings"] }),
  });

  const [form, setForm] = useState<Partial<Company>>({});
  const [automatik, setAutomatik] = useState<Record<string, number>>({});
  // Arbeitszeiten - Grundlage für "Freie Zeiten vorschlagen" beim Termin.
  const [arbeitszeit, setArbeitszeit] = useState<Arbeitszeit>(ARBEITSZEIT_STANDARD);
  useEffect(() => {
    if (data) {
      setForm(data);
      setAutomatik(automatikWerte(data.sla));
      setArbeitszeit(arbeitszeitWerte(data.sla));
    }
  }, [data]);

  if (isLoading || !data) return <p className="text-slate-500">Lädt…</p>;

  function field<K extends keyof Company>(key: K, value: Company[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <h1 className="page-title">Einstellungen</h1>

      <section className="card">
        <h2 className="section-title mb-4">
          <Building2 className="h-3.5 w-3.5 text-slate-400" /> Firma
        </h2>
        <div className="space-y-3">
          <Row label="Firmenname">
            <input
              className="input"
              value={form.name ?? ""}
              onChange={(e) => field("name", e.target.value)}
            />
          </Row>
          <Row label="Telefon">
            <input
              className="input"
              value={form.phone ?? ""}
              onChange={(e) => field("phone", e.target.value)}
            />
          </Row>
          <Row label="E-Mail">
            <input
              className="input"
              value={form.email ?? ""}
              onChange={(e) => field("email", e.target.value)}
            />
          </Row>
          <Row label="Adresse">
            <input
              className="input"
              value={form.address ?? ""}
              onChange={(e) => field("address", e.target.value)}
            />
          </Row>
          <Row label="Tracking-Seite">
            <input
              className="input"
              placeholder="https://www.beispiel.de/kontakt"
              value={form.tracking_page_url ?? ""}
              onChange={(e) => field("tracking_page_url", e.target.value)}
            />
          </Row>
        </div>
      </section>

      <section className="card">
        <h2 className="section-title mb-4">
          <Palette className="h-3.5 w-3.5 text-slate-400" /> Branding
        </h2>
        <div className="space-y-3">
          <Row label="Primärfarbe">
            <div className="flex items-center gap-3">
              <input
                type="color"
                className="h-10 w-14 cursor-pointer rounded-lg border border-slate-200"
                value={form.primary_color ?? "#1a1a2e"}
                onChange={(e) => field("primary_color", e.target.value)}
              />
              <span className="font-mono text-sm text-slate-600">
                {form.primary_color}
              </span>
            </div>
          </Row>
          <Row label="Anrede">
            <select
              className="input"
              value={form.greeting_style ?? "sie"}
              onChange={(e) =>
                field("greeting_style", e.target.value as "sie" | "du")
              }
            >
              <option value="sie">Sie</option>
              <option value="du">Du</option>
            </select>
          </Row>
        </div>
      </section>

      <section className="card">
        <h2 className="section-title mb-4">
          <Mail className="h-3.5 w-3.5 text-slate-400" /> E-Mail
        </h2>
        <div className="space-y-3">
          <Row label="Absendername">
            <input
              className="input"
              value={form.email_sender_name ?? ""}
              onChange={(e) => field("email_sender_name", e.target.value)}
            />
          </Row>
          <Row label="Benachrichtigung an">
            <input
              className="input"
              value={form.notification_email ?? ""}
              onChange={(e) => field("notification_email", e.target.value)}
            />
          </Row>
          <Row label="Signatur">
            <textarea
              className="input h-24"
              value={form.email_signature ?? ""}
              onChange={(e) => field("email_signature", e.target.value)}
            />
          </Row>
          <Row label="Notfall-Benachrichtigung">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand/30"
              checked={form.notfall_notification ?? true}
              onChange={(e) => field("notfall_notification", e.target.checked)}
            />
          </Row>
          <Row label="Öffentlicher Bewertungslink">
            <input
              className="input"
              placeholder="z. B. Ihr Google-Profil zum Bewerten"
              value={form.bewertung_link ?? ""}
              onChange={(e) => field("bewertung_link", e.target.value)}
            />
            <p className="mt-1 px-1 text-xs text-slate-500">
              Kunden, die Ihnen 4 oder 5 Sterne geben, werden gefragt, ob sie
              die Bewertung öffentlich wiederholen möchten. Leer lassen = kein
              Hinweis.
            </p>
          </Row>
          <Row label="Link zur Datenschutzerklärung">
            <input
              className="input"
              placeholder="https://ihre-website.de/datenschutz"
              value={form.datenschutz_url ?? ""}
              onChange={(e) => field("datenschutz_url", e.target.value)}
            />
            <p className="mt-1 px-1 text-xs text-slate-500">
              Steht unter dem Anfrage-Formular. Ihr Formular erhebt Name,
              Telefon und E-Mail – dafür ist ein Datenschutzhinweis
              vorgeschrieben. Ohne Link erscheint nur der allgemeine Satz.
            </p>
          </Row>

          {/* Rechnungsangaben: ohne Steuernummer/USt-IdNr und IBAN kann keine
              gültige E-Rechnung erzeugt werden. */}
          <div className="col-span-full mt-2 border-t border-slate-200 pt-4">
            <p className="mb-1 text-sm font-semibold text-slate-900">Rechnungsangaben</p>
            <p className="mb-3 text-xs text-slate-500">
              Pflicht für Rechnungen und die E-Rechnung (XRechnung). Steuernummer
              oder USt-IdNr genügt - eines von beiden.
            </p>
          </div>
          <Row label="Steuernummer">
            <input className="input" placeholder="z. B. 12/345/67890"
              value={form.rechnung_steuernummer ?? ""}
              onChange={(e) => field("rechnung_steuernummer", e.target.value)} />
          </Row>
          <Row label="USt-IdNr">
            <input className="input" placeholder="z. B. DE123456789"
              value={form.rechnung_ustid ?? ""}
              onChange={(e) => field("rechnung_ustid", e.target.value)} />
          </Row>
          <Row label="IBAN">
            <input className="input" placeholder="DE.."
              value={form.rechnung_iban ?? ""}
              onChange={(e) => field("rechnung_iban", e.target.value)} />
          </Row>
          <Row label="BIC (freiwillig)">
            <input className="input"
              value={form.rechnung_bic ?? ""}
              onChange={(e) => field("rechnung_bic", e.target.value)} />
          </Row>
          <Row label="Zahlungsziel (Tage)">
            <input className="input" type="number" min={0} max={120}
              value={form.rechnung_zahlungsziel_tage ?? 14}
              onChange={(e) => field("rechnung_zahlungsziel_tage", Number(e.target.value))} />
          </Row>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <button
            className="btn-primary"
            onClick={() =>
              save.mutate({
                name: form.name,
                phone: form.phone,
                email: form.email,
                address: form.address,
                trackingPageUrl: form.tracking_page_url,
                primaryColor: form.primary_color,
                greetingStyle: form.greeting_style,
                notificationEmail: form.notification_email,
                bewertungLink: form.bewertung_link,
                datenschutzUrl: form.datenschutz_url,
                arbeitszeiten: arbeitszeit,
                automatik,
                emailSenderName: form.email_sender_name,
                emailSignature: form.email_signature,
                notfallNotification: form.notfall_notification,
                rechnungSteuernummer: form.rechnung_steuernummer,
                rechnungUstid: form.rechnung_ustid,
                rechnungIban: form.rechnung_iban,
                rechnungBic: form.rechnung_bic,
                rechnungZahlungszielTage: form.rechnung_zahlungsziel_tage ?? 14,
              })
            }
            disabled={save.isPending}
          >
            Speichern
          </button>
          {save.isSuccess && (
            <span className="inline-flex items-center gap-1 text-sm text-emerald-600">
              <Check className="h-4 w-4" /> Gespeichert
            </span>
          )}
        </div>
      </section>

      {/* Arbeitszeiten: daraus rechnet der AuftragsManager freie Termine aus. */}
      <section className="card">
        <h2 className="section-title mb-4">
          <CalendarClock className="h-3.5 w-3.5 text-slate-400" /> Arbeitszeiten
        </h2>
        <p className="mb-4 text-xs text-slate-500">
          Danach sucht der Knopf „Freie Zeiten vorschlagen" beim Termin
          passende Lücken – Termine, die schon vergeben sind, lässt er aus.
          Gespeichert wird mit dem Speichern-Knopf oben.
        </p>
        <div className="space-y-3">
          <Row label="Arbeitstage">
            <div className="flex flex-wrap gap-1">
              {WOCHENTAGE.map((t) => {
                const an = arbeitszeit.tage.includes(t.nr);
                return (
                  <button
                    key={t.nr}
                    type="button"
                    aria-pressed={an}
                    onClick={() =>
                      setArbeitszeit((a) => ({
                        ...a,
                        tage: an ? a.tage.filter((x) => x !== t.nr) : [...a.tage, t.nr].sort(),
                      }))
                    }
                    className={
                      "h-8 w-10 rounded-lg border text-xs font-medium " +
                      (an
                        ? "border-brand bg-brand/10 text-brand"
                        : "border-slate-200 bg-white text-slate-500 hover:border-slate-300")
                    }
                  >
                    {t.kurz}
                  </button>
                );
              })}
            </div>
          </Row>
          <Row label="Arbeitszeit">
            <div className="flex items-center gap-2">
              <input type="time" className="input w-auto" value={arbeitszeit.von}
                onChange={(e) => setArbeitszeit((a) => ({ ...a, von: e.target.value }))} />
              <span className="text-sm text-slate-500">bis</span>
              <input type="time" className="input w-auto" value={arbeitszeit.bis}
                onChange={(e) => setArbeitszeit((a) => ({ ...a, bis: e.target.value }))} />
            </div>
          </Row>
          <Row label="Mittagspause">
            <div className="flex items-center gap-2">
              <input type="time" className="input w-auto" value={arbeitszeit.pauseVon}
                onChange={(e) => setArbeitszeit((a) => ({ ...a, pauseVon: e.target.value }))} />
              <span className="text-sm text-slate-500">bis</span>
              <input type="time" className="input w-auto" value={arbeitszeit.pauseBis}
                onChange={(e) => setArbeitszeit((a) => ({ ...a, pauseBis: e.target.value }))} />
            </div>
            <p className="mt-1 px-1 text-xs text-slate-500">
              Beide Felder leeren = durchgehend arbeiten.
            </p>
          </Row>
          <Row label="Fahrzeit zwischen Terminen">
            <div className="flex items-center gap-2">
              <input type="number" min={0} max={180} className="input w-24"
                value={arbeitszeit.puffer}
                onChange={(e) => setArbeitszeit((a) => ({ ...a, puffer: Math.max(0, Number(e.target.value) || 0) }))} />
              <span className="text-sm text-slate-500">Minuten</span>
            </div>
            <p className="mt-1 px-1 text-xs text-slate-500">
              So viel Luft bleibt vor und nach jedem bestehenden Termin frei.
            </p>
          </Row>
        </div>
      </section>

      <section className="card">
        <h2 className="section-title mb-4">
          <Timer className="h-3.5 w-3.5 text-slate-400" /> Automatik
        </h2>
        <p className="mb-4 text-xs text-slate-500">
          Wann das System von selbst erinnert, nachfasst und aufräumt.
          Alle Werte gelten nur für Ihren Betrieb. <b>0 = aus.</b>
          Gespeichert wird mit dem Speichern-Knopf oben.
        </p>
        <div className="space-y-3">
          {AUTOMATIK_FELDER.map((f) => (
            <Row key={f.key} label={f.label}>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  className="input w-28"
                  value={automatik[f.key] ?? f.standard}
                  onChange={(e) =>
                    setAutomatik((v) => ({ ...v, [f.key]: Math.max(0, Number(e.target.value) || 0) }))
                  }
                />
                <span className="text-sm text-slate-500">{f.einheit}</span>
                {(automatik[f.key] ?? f.standard) === 0 && (
                  <span className="pill bg-slate-100 text-slate-500 ring-1 ring-slate-200">aus</span>
                )}
              </div>
              <p className="mt-1 px-1 text-xs text-slate-500">{f.hinweis}</p>
            </Row>
          ))}
        </div>
      </section>

      <Team />
    </div>
  );
}

interface TeamUser {
  id: string;
  name: string;
  role: "admin" | "staff";
  created_at: string;
  email: string;
  lastSignIn: string | null;
}

function Team() {
  const session = loadSession();
  const isAdmin = session?.user.role === "admin";
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["team"],
    queryFn: () => api<{ items: TeamUser[] }>("/admin/users"),
  });

  const [form, setForm] = useState({
    name: "",
    email: "",
    role: "staff" as "staff" | "admin",
  });
  const [error, setError] = useState<string | null>(null);

  const addUser = useMutation({
    mutationFn: () => api("/admin/users", { method: "POST", body: form }),
    onSuccess: () => {
      setForm({ name: "", email: "", role: "staff" });
      setError(null);
      qc.invalidateQueries({ queryKey: ["team"] });
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Fehler"),
  });

  const removeUser = useMutation({
    mutationFn: (id: string) => api(`/admin/users/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["team"] }),
  });

  return (
    <section className="card">
      <h2 className="section-title mb-4">
        <Users className="h-3.5 w-3.5 text-slate-400" /> Team
        <span className="ml-1 text-slate-400">({data?.items.length ?? 0})</span>
      </h2>

      <ul className="divide-y divide-slate-100">
        {data?.items.map((u) => (
          <li
            key={u.id}
            className="flex items-center justify-between py-3 text-sm"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
                {initials(u.name)}
              </div>
              <div>
                <div className="font-medium text-slate-900">
                  {u.name}
                  {u.id === session?.user.id && (
                    <span className="ml-2 text-xs text-slate-400">(Sie)</span>
                  )}
                </div>
                <div className="text-xs text-slate-500">
                  {u.role === "admin" ? "Geschäftsführer" : "Mitarbeiter"}
                  {u.email ? ` · ${u.email}` : ""} ·{" "}
                  {u.lastSignIn
                    ? `zuletzt angemeldet ${new Date(u.lastSignIn).toLocaleDateString("de-DE")}`
                    : "noch nie angemeldet"}
                </div>
              </div>
            </div>
            {isAdmin && u.id !== session?.user.id && (
              <button
                className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
                onClick={() => {
                  if (confirm(`${u.name} wirklich entfernen?`))
                    removeUser.mutate(u.id);
                }}
              >
                <Trash2 className="h-3.5 w-3.5" /> Entfernen
              </button>
            )}
          </li>
        ))}
      </ul>

      {isAdmin && (
        <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <h3 className="mb-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
            <UserPlus className="h-3.5 w-3.5" /> Neuen Mitarbeiter hinzufügen
          </h3>
          <div className="grid gap-2 sm:grid-cols-2">
            <input
              className="input"
              placeholder="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <input
              className="input"
              type="email"
              placeholder="E-Mail"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <select
              className="input"
              value={form.role}
              onChange={(e) =>
                setForm({ ...form, role: e.target.value as "staff" | "admin" })
              }
            >
              <option value="staff">Mitarbeiter (sieht nur zugewiesene Aufträge)</option>
              <option value="admin">Geschäftsführer (sieht alles, darf zuweisen)</option>
            </select>
          </div>
          {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
          <button
            className="btn-primary btn-sm mt-3"
            disabled={
              !form.name ||
              !form.email ||
              addUser.isPending
            }
            onClick={() => addUser.mutate()}
          >
            Hinzufügen
          </button>
        </div>
      )}
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="grid gap-2 sm:grid-cols-[180px_1fr] sm:items-center">
      <span className="text-sm text-slate-600">{label}</span>
      <div>{children}</div>
    </label>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}
